import { computeCameraSnapshotForBounds } from '@/core/cameraFit'
import { hydrateMeta, initialMeta } from '@/document/hydration'
import type { SnapshotMeta } from '@/document/model'
import { createDocumentBase } from '@/storage/grid/documentBase'
import type { Position } from '@/voxelMath'
import type { VoxelData, VoxelMap } from '@/voxelTypes'

export type PageDocumentOptions = {
  /** The page id (PageDefinition.id) and its format version. */
  page: { id: string; version: number }
  bounds: Position
  palette: VoxelData[]
  backgroundColor: number
  /**
   * Camera polar angle (0 = straight down), azimuth and zoom, fitted to the bounds; `target` the point
   * looked at (default the bounds centre). `fitHeight`: frame the world as if it were only this tall,
   * for a tall world whose height is room to build into (025's stacking), not something to show.
   * `fit: 'footprint'`: frame the floor's rectangle to the screen (its width across, its depth up,
   * `zoom` the share of the screen it fills) instead of the bounds' sphere, for a flat world in the
   * screen's shape (`canvasFootprint`); assumes `theta` 0 (looking up the z axis). `inset`: px along
   * the top and bottom edges kept for the page's bars; the rectangle is fitted to and centred in the
   * screen between them (the target moves). `fit: 'wall'`: the same for a world that stands up, a
   * wall seen nearly face on (`phi` near pi/2): its width across and its height (`bounds.y`) up.
   */
  camera?: {
    phi?: number
    theta?: number
    zoom?: number
    margin?: number
    target?: Position
    fitHeight?: number
    fit?: 'sphere' | 'footprint' | 'wall'
    inset?: { top?: number; bottom?: number }
  }
  /** Sun position; defaults to high and slightly behind. */
  light?: Position
  /** Grids and the bounds border; off by default (pages draw their own world). */
  grids?: boolean
  /** Extra meta fields (e.g. `mirrors`). */
  meta?: Partial<SnapshotMeta>
}

/** A page document from cells and a few look settings (the boilerplate every page shares). */
export function createPageDocument(entries: Iterable<[number, VoxelData]>, opts: PageDocumentOptions): { base: VoxelMap; meta: SnapshotMeta } {
  const { bounds } = opts
  const base = createDocumentBase(Array.from(entries), undefined, bounds)
  const cam = opts.camera ?? {}
  const grids = opts.grids ?? false
  const fitBounds = cam.fitHeight ? { ...bounds, y: Math.min(bounds.y, cam.fitHeight) } : bounds
  const phi = cam.phi ?? 0.8
  const margin = cam.margin ?? 0.92
  const footprint = cam.fit === 'footprint' || cam.fit === 'wall' ? footprintFrame(fitBounds, phi, margin, cam.zoom ?? 0.92, cam.inset ?? {}, cam.fit) : null
  const zoom = footprint?.zoom ?? cam.zoom ?? 0.92
  const target = cam.target ?? footprint?.target
  const meta = hydrateMeta(
    {
      ...initialMeta(),
      bounds,
      palette: opts.palette.map((item) => ({ ...item })),
      backgroundColor: opts.backgroundColor,
      camera: computeCameraSnapshotForBounds(fitBounds, {
        fov: FOV,
        phi,
        theta: cam.theta ?? 0.35,
        margin,
        zoom,
        cameraMode: 'orbit',
        ...(target ? { target } : {}),
      }),
      lighting: { directional: { position: opts.light ?? { x: bounds.x * 0.2, y: bounds.y * 2.2 + 40, z: -bounds.z * 0.4 } } },
      ...(grids ? {} : {
        boundsAppearance: {
          baseGrid: { mode: 'off' },
          wallGrid: { mode: 'off' },
          border: { visible: false },
        },
      }),
      ...opts.meta,
      experience: { id: opts.page.id, version: opts.page.version },
    } as SnapshotMeta,
    'lenient',
  )
  return { base, meta }
}

const FOV = 45

/**
 * The orbit fit's `zoom` (and target) that frames the floor rectangle of `bounds`, seen from polar
 * angle `phi` at azimuth 0, so it fills `fill` of the screen between the insets in its tighter
 * direction. The orbit fit sizes its distance by the bounds' sphere in the narrower field of view,
 * which leaves a sheet in the screen's shape small; this asks for the distance at which the near
 * edge (the closest, so the widest) just fits across and the near half just fits up. `wall`: the
 * rectangle is the upright x-y face (its height `bounds.y`), seen at `pi/2 - phi` off its normal.
 */
function footprintFrame(
  bounds: Position,
  phi: number,
  margin: number,
  fill: number,
  inset: { top?: number; bottom?: number },
  plane: 'footprint' | 'wall' = 'footprint',
): { zoom: number; target: Position } {
  const wall = plane === 'wall'
  // The angle between the view and the rectangle's normal (straight down for a floor, along -z for a wall).
  const tilt = wall ? Math.PI / 2 - phi : phi
  const w = typeof window !== 'undefined' && window.innerWidth > 0 ? window.innerWidth : 1280
  const h = typeof window !== 'undefined' && window.innerHeight > 0 ? window.innerHeight : 800
  const top = inset.top ?? 0
  const bottom = inset.bottom ?? 0
  const free = Math.max(h * 0.4, h - top - bottom)
  const vFov = (FOV * Math.PI) / 180
  const tanV = Math.tan(vFov / 2)
  const tanH = tanV * Math.max(0.1, w / h)
  const tanFree = tanV * (free / h)
  const halfW = bounds.x / 2 / fill
  const halfD = (wall ? bounds.y : bounds.z) / 2 / fill
  const distance = Math.max(halfW / tanH, (halfD * Math.cos(tilt)) / tanFree) + halfD * Math.abs(Math.sin(tilt))
  // What the orbit fit gives at zoom 1 (shared/camera-fit.mjs): the sphere in the narrower angle.
  const hFov = 2 * Math.atan(tanH)
  const halfDiag = 0.5 * Math.hypot(bounds.x, bounds.y, bounds.z)
  const sphere = (halfDiag / Math.sin(Math.max(0.2, Math.min(vFov, hFov) / 2))) * margin
  // Centre the sheet between the insets: the camera sits on the +z side looking toward -z (up the
  // screen), so a sheet to show lower looks at a point further up the sheet (higher up a wall).
  const unitsPerPx = (2 * distance * tanV) / h
  const shift = (((top - bottom) / 2) * unitsPerPx) / Math.cos(tilt)
  const target = wall ? { x: bounds.x / 2, y: bounds.y / 2 + shift, z: bounds.z / 2 } : { x: bounds.x / 2, y: bounds.y / 2, z: bounds.z / 2 - shift }
  return { zoom: distance / sphere, target }
}
